import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Skeleton,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useThemeColors } from "../../../hooks/useThemeColors";
import apiApplicationService from "../../../service/apiApplicationService";
import permissionService from "../../../service/permissionService";
import { Permission } from "../roles/types/permissions";
import { useApiApplicationGrantsPicker } from "./useApiApplicationGrantsPicker";
import { useExternalResources } from "./useExternalResources";
import ApiApplicationGrantPicker from "./ApiApplicationGrantPicker";
import ApiTableChecklist from "./ApiTableChecklist";
import GrantedSummaryPanel from "./GrantedSummaryPanel";
import { ApiApplicationGrant, ExternalResource } from "./types";
import TokenRevealDialog from "./TokenRevealDialog";
import ApiResourceDocsDialog from "./ApiResourceDocsDialog";

interface ApiApplicationFormProps {
  mode: "create" | "edit";
}

const viewGrant = (r: ExternalResource): ApiApplicationGrant => ({
  app_label: r.app_label,
  model: r.model,
  can_view: true,
  can_add: false,
  can_change: false,
  can_delete: false,
});

const ApiApplicationForm: React.FC<ApiApplicationFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const colors = useThemeColors();
  const accentColor = colors.primary[400];
  const headingColor = colors.primary[100];

  // Three independent loads: the application (edit only), the Django
  // permission list (advanced picker), and the external API catalog.
  const [appLoading, setAppLoading] = useState(mode === "edit");
  const [appError, setAppError] = useState<string | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  const { resources, loading: resourcesLoading, error: resourcesError, retry: retryResources } =
    useExternalResources();

  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hostInput, setHostInput] = useState("");
  const [hosts, setHosts] = useState<string[]>([]);
  const [wildcard, setWildcard] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [tokenPrefix, setTokenPrefix] = useState<string | null>(null);
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [grants, setGrants] = useState<Record<string, ApiApplicationGrant>>({});
  const [revealToken, setRevealToken] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  const [docsOpen, setDocsOpen] = useState(false);
  const [docsResource, setDocsResource] = useState<ExternalResource | null>(null);
  const [docsTryIt, setDocsTryIt] = useState(false);

  const loadPermissions = useCallback(() => {
    setPermissionsLoading(true);
    setPermissionsError(null);
    permissionService
      .getAllPermissions()
      .then(setAllPermissions)
      .catch(() => setPermissionsError("Could not load the model list."))
      .finally(() => setPermissionsLoading(false));
  }, []);

  useEffect(loadPermissions, [loadPermissions]);

  const loadApplication = useCallback(() => {
    if (mode !== "edit" || !id) return;
    setAppLoading(true);
    setAppError(null);
    apiApplicationService
      .get(Number(id))
      .then((app) => {
        setName(app.name);
        setDescription(app.description);
        setIsActive(app.is_active);
        setTokenPrefix(app.token_prefix);
        if (app.allowed_hosts.includes("*")) {
          setWildcard(true);
        } else {
          setHosts(app.allowed_hosts);
        }
        const grantMap: Record<string, ApiApplicationGrant> = {};
        (app.grants || []).forEach((g) => {
          grantMap[`${g.app_label}.${g.model}`] = g;
        });
        setGrants(grantMap);
      })
      .catch(() => setAppError("Failed to load this application."))
      .finally(() => setAppLoading(false));
  }, [mode, id]);

  useEffect(loadApplication, [loadApplication]);

  // New applications start with read-only access to the core business tables.
  useEffect(() => {
    if (mode !== "create" || resourcesLoading || prefilled || resources.length === 0) return;
    setPrefilled(true);
    const seeded: Record<string, ApiApplicationGrant> = {};
    resources.filter((r) => r.prefill).forEach((r) => {
      seeded[r.key] = viewGrant(r);
    });
    setGrants((prev) => ({ ...seeded, ...prev }));
  }, [mode, resourcesLoading, prefilled, resources]);

  const picker = useApiApplicationGrantsPicker(allPermissions, grants, setGrants, resources);

  const openDocs = (resource: ExternalResource | null, tryIt = false) => {
    setDocsResource(resource);
    setDocsTryIt(tryIt);
    setDocsOpen(true);
  };

  const toggleView = (resource: ExternalResource) => picker.toggleVerb(resource.app_label, resource.model, "view");
  const revokeView = (resource: ExternalResource) => {
    if (grants[resource.key]?.can_view) toggleView(resource);
  };
  const setAllApiTables = (granted: boolean) => {
    setGrants((prev) => {
      const next = { ...prev };
      resources.forEach((r) => {
        if (granted) next[r.key] = { ...(next[r.key] ?? viewGrant(r)), can_view: true };
        else if (next[r.key]) next[r.key] = { ...next[r.key], can_view: false };
      });
      return next;
    });
  };

  const addHost = () => {
    const value = hostInput.trim();
    if (!value) return;
    if (!hosts.includes(value)) setHosts((prev) => [...prev, value]);
    setHostInput("");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required", { position: "top-right" });
      return;
    }
    const allowed_hosts = wildcard ? ["*"] : hosts;
    if (allowed_hosts.length === 0) {
      toast.error("Add at least one allowed host, or enable wildcard access", { position: "top-right" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description,
        allowed_hosts,
        is_active: isActive,
        grants: picker.grantsList,
      };
      if (mode === "create") {
        const result = await apiApplicationService.create(payload);
        toast.success(`"${result.name}" created`, { position: "top-right" });
        setRevealToken(result.token);
      } else if (id) {
        const updated = await apiApplicationService.update(Number(id), payload);
        const grantMap: Record<string, ApiApplicationGrant> = {};
        (updated.grants || []).forEach((g) => {
          grantMap[`${g.app_label}.${g.model}`] = g;
        });
        setGrants(grantMap);
        toast.success("Saved", { position: "top-right" });
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: Record<string, string[]> } })?.response?.data;
      toast.error(data?.allowed_hosts?.[0] || data?.name?.[0] || "Failed to save application", {
        position: "top-right",
      });
    } finally {
      setSaving(false);
    }
  };

  const grantedCount = picker.grantsList.length;
  const prefillCount = resources.filter((r) => r.prefill).length;
  const effectiveHosts = wildcard ? ["*"] : hosts;
  const busy = saving || appLoading;

  if (appError) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, textAlign: "center" }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {appError}
        </Typography>
        <Button variant="outlined" onClick={loadApplication}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            {appLoading ? (
              <>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={80} />
              </>
            ) : (
              <>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  disabled={saving}
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={saving}
                />
                {mode === "edit" && (
                  <FormControlLabel
                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    label={isActive ? "Active" : "Revoked"}
                  />
                )}
              </>
            )}

            <Paper sx={{ boxShadow: 0, border: "1px solid", borderColor: "divider", borderRadius: 3, p: 2.5 }}>
              <Typography fontWeight={700} sx={{ mb: 0.5, color: headingColor }}>
                Allowed Hosts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Origins this token may be used from.
              </Typography>
              {appLoading ? (
                <Skeleton variant="rounded" height={40} />
              ) : (
                <>
                  <FormControlLabel
                    control={<Switch checked={wildcard} onChange={(e) => setWildcard(e.target.checked)} />}
                    label='Allow all origins ("*")'
                  />
                  {wildcard ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      Wildcard access is blocked in production. The server rejects it outside staging and
                      development environments.
                    </Alert>
                  ) : (
                    <Box sx={{ mt: 1 }}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          placeholder="e.g. staging.example.com"
                          value={hostInput}
                          onChange={(e) => setHostInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addHost();
                            }
                          }}
                          fullWidth
                        />
                        <Button variant="outlined" onClick={addHost} disabled={!hostInput.trim()}>
                          Add
                        </Button>
                      </Stack>
                      <Stack direction="row" flexWrap="wrap" mt={1} gap={1}>
                        {hosts.map((host) => (
                          <Chip
                            key={host}
                            label={host}
                            onDelete={() => setHosts((prev) => prev.filter((h) => h !== host))}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Paper>

            <Box>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={1}
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography fontWeight={700} sx={{ color: headingColor }}>
                    API tables
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tick the tables this token may read.
                    {mode === "create" && prefillCount > 0
                      ? ` ${prefillCount} core tables are prefilled; adjust as needed.`
                      : ""}
                  </Typography>
                </Box>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Button size="small" onClick={() => setAllApiTables(true)} disabled={busy || resourcesLoading}>
                    Grant all
                  </Button>
                  <Button size="small" onClick={() => setAllApiTables(false)} disabled={busy || resourcesLoading}>
                    Clear all
                  </Button>
                </Stack>
              </Stack>
              <ApiTableChecklist
                resources={resources}
                grants={grants}
                loading={resourcesLoading || appLoading}
                error={resourcesError}
                onRetry={retryResources}
                onToggleView={toggleView}
                onShowDocs={(r) => openDocs(r)}
                onTry={(r) => openDocs(r, true)}
                disabled={saving}
              />
            </Box>

            <Box>
              <Button
                size="small"
                onClick={() => setAdvancedOpen((v) => !v)}
                endIcon={advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ color: accentColor }}
              >
                Advanced: all Django models
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Grants on models without an external endpoint have no effect yet. {grantedCount} model
                {grantedCount === 1 ? "" : "s"} granted in total.
              </Typography>
              <Collapse in={advancedOpen} unmountOnExit>
                {permissionsError ? (
                  <Alert
                    severity="warning"
                    action={
                      <Button size="small" onClick={loadPermissions}>
                        Retry
                      </Button>
                    }
                  >
                    {permissionsError}
                  </Alert>
                ) : (
                  <>
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Search apps or models…"
                        value={picker.searchTerm}
                        onChange={(e) => picker.setSearchTerm(e.target.value)}
                        sx={{ flexGrow: 1, minWidth: 200 }}
                        disabled={permissionsLoading}
                      />
                      <Button size="small" onClick={() => picker.bulk("readOnly")} disabled={permissionsLoading}>
                        Read-only on visible
                      </Button>
                      <Button size="small" onClick={() => picker.bulk("clearAll")} disabled={permissionsLoading}>
                        Clear visible
                      </Button>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={picker.showAllModels}
                            onChange={(e) => picker.setShowAllModels(e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2" color="text.secondary">All models</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={picker.showEventModels}
                            onChange={(e) => picker.setShowEventModels(e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2" color="text.secondary">Audit models</Typography>}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={picker.showSystemApps}
                            onChange={(e) => picker.setShowSystemApps(e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2" color="text.secondary">System apps</Typography>}
                      />
                    </Stack>
                    {permissionsLoading ? (
                      <Stack spacing={1.5}>
                        {[0, 1, 2].map((i) => (
                          <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: 3 }} />
                        ))}
                      </Stack>
                    ) : (
                      <ApiApplicationGrantPicker
                        visibleApps={picker.visibleApps}
                        pagedApps={picker.pagedApps}
                        pagination={picker.pagination}
                        setPagination={picker.setPagination}
                        grants={grants}
                        expandedApps={picker.expandedApps}
                        toggleApp={picker.toggleApp}
                        toggleVerb={picker.toggleVerb}
                        resourceFor={picker.resourceFor}
                        onShowDocs={(r) => openDocs(r)}
                        accentColor={accentColor}
                      />
                    )}
                  </>
                )}
              </Collapse>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => navigate("/configurations/api-applications")} disabled={saving}>
                {mode === "create" ? "Cancel" : "Back to list"}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={busy || resourcesLoading}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saving ? "Saving…" : mode === "create" ? "Create Application" : "Save Changes"}
              </Button>
            </Stack>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ order: { xs: -1, md: 0 } }}>
          <GrantedSummaryPanel
            resources={resources}
            grants={grants}
            loading={resourcesLoading || appLoading}
            tokenPrefix={tokenPrefix}
            hosts={hosts}
            wildcard={wildcard}
            onShowDocs={(r) => openDocs(r)}
            onTry={(r) => openDocs(r, true)}
            onRevoke={revokeView}
          />
        </Grid>
      </Grid>

      <TokenRevealDialog
        token={revealToken}
        onShowDocs={() => openDocs(resources.find((r) => grants[r.key]?.can_view) ?? null, true)}
        onClose={() => {
          setRevealToken(null);
          navigate("/configurations/api-applications");
        }}
      />
      <ApiResourceDocsDialog
        open={docsOpen}
        onClose={() => setDocsOpen(false)}
        resource={docsResource}
        resources={resources}
        hosts={effectiveHosts.filter((h) => h !== "*")}
        tokenPrefix={tokenPrefix ?? undefined}
        startWithTryIt={docsTryIt}
      />
    </Box>
  );
};

export default ApiApplicationForm;
